import {gql} from '@apollo/client';

import {assert} from 'Asserts';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  StructuredTableKind,
  StructuredTableMetadata,
} from 'notebook/structured-tables/StructuredTableConstants';
import kindToTableType from 'notebook/structured-tables/util/kindToTableType';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  TransferTableValidateDestinationPlateQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';
import APIUtil from 'util/APIUtil';

import {
  TransferStructuredTableDestinationType,
  TransferStructuredTableMetadata,
} from '../TransferStructuredTableAdapter';
import {
  GetDestinationPlateSchemaQuery,
  GetDestinationPlateSchemaQueryVariables,
} from './__generated/GetDestinationPlateSchemaQuery';
import {GetPlateSizeQuery, GetPlateSizeQueryVariables} from './__generated/GetPlateSizeQuery';
import {GetPlateWellTagsQuery, GetPlateWellTagsQueryVariables} from './__generated/GetPlateWellTagsQuery';
import extractStorableId from './extractStorableId';
import makeCustomResolutionResultFromTypedValue from './makeCustomResolutionResultFromTypedValue';

export type GetWellTagsPlate = ExtractByTypename<GetPlateWellTagsQuery['directStorable'], 'PlateNode'>;

export const GET_DESTINATION_PLATE_SCHEMA_QUERY = gql`
  query GetDestinationPlateSchemaQuery($plateIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $plateIds) {
      ... on PlateNode {
        id
        storableSchema {
          ... on PlateSchemaNode {
            id
            plateSchemaType
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const stubbableFunctions = {
  async doPlateSchemaQuery(plateId: string): Promise<GetDestinationPlateSchemaQuery> {
    return await doQueryWithoutCache<GetDestinationPlateSchemaQuery, GetDestinationPlateSchemaQueryVariables>(
      {query: GET_DESTINATION_PLATE_SCHEMA_QUERY},
      {
        variables: {plateIds: [plateId]},
      }
    );
  },

  async doPlateSizeQuery(plateId: string): Promise<GetPlateSizeQuery> {
    return await doQueryWithoutCache<GetPlateSizeQuery, GetPlateSizeQueryVariables>(
      {query: GET_PLATE_SIZE_QUERY},
      {variables: {plateIds: [plateId]}}
    );
  },

  async doPlateWellTagsQuery(
    plateId: string,
    rowIndex: number,
    colIndex: number
  ): Promise<GetPlateWellTagsQuery> {
    return await doQueryWithoutCache<GetPlateWellTagsQuery, GetPlateWellTagsQueryVariables>(
      {query: GET_PLATE_WELL_TAGS_QUERY},
      {variables: {input: {id: plateId, rowIndex, colIndex}}}
    );
  },
};

export const DestinationPlateResolver = {
  validateDestinationPlate: async (
    query: TransferTableValidateDestinationPlateQuery
  ): Promise<ServerResolutionResult> => {
    const {plateId, plateSchemaId: expectedSchemaId} = query;

    const data = await stubbableFunctions.doPlateSchemaQuery(plateId);
    const plate = data.storables[0];
    if (isInaccessible(plate)) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    assert(plate.__typename === 'PlateNode', 'Expected a plate');
    assert(plate.storableSchema.__typename === 'PlateSchemaNode', 'Expected a plate schema');
    const schemaId = plate.storableSchema.id;
    if (schemaId === expectedSchemaId) {
      return makeCustomResolutionResultFromTypedValue(null);
    }

    return {
      type: undefined,
      error: 'Not a valid plate',
      resolvedValue: undefined,
    };
  },
};

export function destinationPlateCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: StructuredTableMetadata & TransferStructuredTableMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: stringResolvedValue} = resolveTypedValueToType(columnDefinition, typedValue, {
    tableType: kindToTableType(StructuredTableKind.TRANSFER, false),
  });
  if (stringResolvedValue.type !== BaseTypedValueType.STORABLES) {
    return {val: typedValue, error: null, locked: false};
  }

  const plateId = extractStorableId(stringResolvedValue);
  if (plateId == null || !APIUtil.isPlate(plateId)) {
    // TODO(BNCH-15744): the evaluator currently runs customEval before resolveTypedValue,
    // so by returning the original typed value here, we're falling back to the default validation logic
    // in resolveTypedValue
    return {val: typedValue, error: null, locked: false};
  }

  assert(
    tableMetadata.destinationType === TransferStructuredTableDestinationType.PLATE,
    'Plate Column should not exist if destination type is not plate.'
  );
  const plateSchemaId = tableMetadata.schemaMetadata.find((metadata) =>
    APIUtil.isPlateSchema(metadata.schemaId)
  )?.schemaId;
  assert(plateSchemaId != null, 'Plate schema id must not be null if destination type is plate.');

  // Check resolved value has same schema as destination type
  const {resolvedValue, error} = resolveFromResolutionQuery(
    stringResolvedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.TRANSFER_TABLE_VALIDATE_DESTINATION_PLATE,
      plateId,
      type: ResolutionQueryType.CUSTOM,
      plateSchemaId,
    },
    makeTypedValueFromCustomResolvedValue
  );

  return {val: resolvedValue, error, locked: false};
}

const GET_PLATE_SIZE_QUERY = gql`
  query GetPlateSizeQuery($plateIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    plates(ids: $plateIds) {
      ... on PlateNode {
        id
        storableSchema {
          ... on PlateSchemaNode {
            id
            plateSchemaType
            height
            width
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const GET_PLATE_WELL_TAGS_QUERY = gql`
  query GetPlateWellTagsQuery($input: StorableInput!) @skipAudit @engComponent(name: INVENTORY) {
    directStorable(input: $input) {
      ... on PlateNode {
        id
        cell {
          ...GridCellIndexesGraphQL
          ... on GridCellNode {
            container {
              ... on ContainerNode {
                id
                tagsJson
              }
              ...InaccessibleInfoGraphQL
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('GridCellIndexes').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;
